/**
 * PayoutProofCTA — top-of-dashboard call-to-action driving members to the
 * community payout-proof YouTube video. Designed to be impossible to miss:
 *
 *   • High-contrast gold→amber gradient button
 *   • Continuous smooth pulse + glow animation (transform/opacity/box-shadow
 *     only — no `transition: all`, per design guidelines)
 *   • Respects ``prefers-reduced-motion`` (animation pauses for users who
 *     have that OS-level accessibility preference enabled)
 *   • Opens in a new tab with ``rel="noopener noreferrer"`` so the SPA
 *     state survives and we don't leak the opener context
 *
 * Self-contained styles via a single ``<style>`` block keyed on a
 * widget-scoped class name so the keyframes can't collide with anything
 * else in the bundle.
 */
const VIDEO_URL = 'https://youtu.be/HCPANiKDpx8?si=RF1yqi9WkC6xISPv';

const PayoutProofCTA = () => {
  return (
    <section className="payout-proof-cta-root mb-6 md:mb-7" data-testid="payout-proof-cta">
      <style>{`
        @keyframes payoutProofPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow:
              0 0 0 0 rgba(255, 182, 18, 0.65),
              0 18px 44px -10px rgba(255, 182, 18, 0.55),
              0 0 28px rgba(255, 194, 14, 0.32);
          }
          50% {
            transform: scale(1.035);
            box-shadow:
              0 0 0 14px rgba(255, 182, 18, 0.00),
              0 22px 60px -10px rgba(255, 182, 18, 0.85),
              0 0 50px rgba(255, 194, 14, 0.55);
          }
        }
        @keyframes payoutProofShimmer {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .payout-proof-cta-btn {
          background: linear-gradient(
            115deg,
            #FFB612 0%,
            #FFD24A 22%,
            #FFC20E 46%,
            #FFE497 68%,
            #FFB612 100%
          );
          background-size: 220% 220%;
          animation:
            payoutProofPulse 1.55s ease-in-out infinite,
            payoutProofShimmer 3.6s linear infinite;
        }
        .payout-proof-cta-btn:hover {
          animation-play-state: paused;
          transform: scale(1.045);
          box-shadow:
            0 0 0 6px rgba(255, 182, 18, 0.18),
            0 26px 70px -12px rgba(255, 182, 18, 0.95),
            0 0 60px rgba(255, 194, 14, 0.70);
        }
        .payout-proof-cta-btn:active {
          transform: scale(0.985);
        }
        .payout-proof-cta-btn:focus-visible {
          outline: 3px solid #FFE08A;
          outline-offset: 4px;
        }
        @media (prefers-reduced-motion: reduce) {
          .payout-proof-cta-btn {
            animation: none;
            box-shadow:
              0 0 0 4px rgba(255, 182, 18, 0.22),
              0 18px 44px -10px rgba(255, 182, 18, 0.65);
          }
        }
      `}</style>

      <div
        className="rounded-2xl p-5 md:p-6 text-center relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(255,194,14,0.08) 0%, rgba(255,182,18,0.02) 100%)',
          border: '1px solid rgba(255, 194, 14, 0.35)',
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 14px 38px -16px rgba(255, 182, 18, 0.35)',
        }}
      >
        <a
          href={VIDEO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="payout-proof-cta-btn inline-flex items-center justify-center gap-2 font-display font-extrabold rounded-full px-7 py-4 md:px-9 md:py-5 text-[15px] md:text-[17px] uppercase tracking-[0.12em] select-none cursor-pointer no-underline"
          style={{
            color: '#0a0a0b',
            letterSpacing: '0.06em',
            textShadow: '0 1px 0 rgba(255,255,255,0.25)',
          }}
          data-testid="payout-proof-cta-button"
          aria-label="Drop your payout proof — opens YouTube video in a new tab"
        >
          📢 Drop Your Payout Proof Here!
        </a>

        <p className="mt-4 text-[12.5px] md:text-[13px] leading-relaxed text-[var(--sc-text-dim)] max-w-xl mx-auto" data-testid="payout-proof-cta-caption">
          Got paid today? Click to watch, like, and leave a comment with today's date to feed the algorithm and show the world our math works!
        </p>
      </div>
    </section>
  );
};

export default PayoutProofCTA;
